import React from 'react';
import {addToast, Button, Dialogue, Input, Modal, Text, copyToClipboard} from '@albert-io/atomic';

interface RequestAuthorizationModalProps {
  /** The assignment the teacher wants authorized. Used to build the deep link. */
  assignmentId?: string;
  assignmentName?: string;
  onClose: () => void;
}

/**
 * Teacher → admin lock-authorization request (Part 7, V1 = shareable link only;
 * an in-app inbox / email is explicitly V2). Produces a copyable deep link to
 * the assignment that the teacher sends to an admin out-of-band; the admin opens
 * it and grants access via the Part 6 surfaces.
 */
export function RequestAuthorizationModal({
  assignmentId,
  assignmentName,
  onClose
}: RequestAuthorizationModalProps) {
  const link = `${global.location.origin}/assignments/${assignmentId ?? ''}/settings`;

  const handleCopy = () => {
    copyToClipboard(link, () =>
      addToast({
        color: 'positive',
        title: 'Link copied',
        message: 'Send it to your school admin to request access.'
      })
    );
  };

  return (
    <Modal ariaLabel='Request student access' handleClose={onClose}>
      {() => (
        <Dialogue inModal size='s' title='Request student access' handleClose={onClose}>
          <Dialogue.Body>
            <Text as='p' className='mb-x2'>
              Send this link to your school admin. They can use it to open
              {assignmentName ? ` “${assignmentName}”` : ' this assignment'} and grant your students
              access to the restricted questions.
            </Text>
            <Input aria-label='Assignment link' value={link} readOnly className='w-full' />
            <Text size='s' color='secondary' className='mt-x1'>
              Can’t copy? Select the link and copy it manually.
            </Text>
          </Dialogue.Body>
          <Dialogue.BtnGroup>
            <Button variant='outlined' color='secondary' onClick={onClose}>
              Done
            </Button>
            <Button onClick={handleCopy}>Copy link</Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
}

export default RequestAuthorizationModal;
